import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KNOWN_REPORTS } from '@/features/dashboard/config';
import { AdminApiError, createUser, updateUser, type AdminUser, type CreateUserInput, type UpdateUserInput } from '@/api/admin';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { TextInput } from '@/components/ui/TextInput';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { DEFAULT_PASSWORD_LENGTH, generatePassword } from './generatePassword';
import styles from './AdminUsersPage.module.css';

/*
 * Client ids in use today, offered as a dropdown so an admin can't invent a
 * variant that no report is served under (a typo in `client_id` silently
 * produces a user who can see nothing). Hardcoded on purpose for now — the
 * backend has no endpoint that enumerates clients; when one exists this list
 * should come from it.
 *
 * "ah" is deliberately NOT offered (2026-08-25 request) even though accounts
 * exist under it: new users go on "albert-heijn". Existing "ah" accounts still
 * open on "Other…" with their value prefilled, so editing one cannot silently
 * move it.
 */
const KNOWN_CLIENT_IDS = ['albert-heijn', 'carrefour-france', 'icemobile'] as const;
const OTHER_CLIENT = '__other__';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CLIENT_ID_RE = /^[a-z][a-z0-9_-]*$/;
const REPORT_ID_RE = /^[a-z][a-z0-9-]*$/;

type Props = {
  mode: 'create' | 'edit';
  user?: AdminUser;
  currentUserId: string | null;
  onClose: () => void;
  onSaved: () => void;
};

export function UserFormModal({ mode, user, currentUserId, onClose, onSaved }: Props) {
  const isSelf = mode === 'edit' && user?.id === currentUserId;

  const [email, setEmail] = useState(user?.email ?? '');
  /*
   * Two pieces of state: what the dropdown shows, and the typed value behind
   * "Other". An EXISTING user whose client_id is not in the list must not lose
   * it — the dropdown opens on "Other" with the value prefilled.
   */
  const existing = user?.client_id ?? '';
  const existingIsKnown = (KNOWN_CLIENT_IDS as readonly string[]).includes(existing);
  const [clientChoice, setClientChoice] = useState<string>(existing === '' ? '' : existingIsKnown ? existing : OTHER_CLIENT);
  const [customClientId, setCustomClientId] = useState(existingIsKnown ? '' : existing);
  const clientId = clientChoice === OTHER_CLIENT ? customClientId : clientChoice;
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'client' | 'admin'>(user?.role ?? 'client');
  const [active, setActive] = useState(user?.active ?? true);
  const [reports, setReports] = useState<Set<string>>(new Set(user?.allowed_reports ?? []));
  const [customReport, setCustomReport] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [revealPassword, setRevealPassword] = useState(false);
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const [generated, setGenerated] = useState(false);

  const knownIds = useMemo(() => new Set(KNOWN_REPORTS.map((r) => r.id)), []);
  const customGranted = useMemo(() => [...reports].filter((r) => !knownIds.has(r)), [reports, knownIds]);

  const mutation = useMutation({
    mutationFn: (vars: { create?: CreateUserInput; patch?: UpdateUserInput }) => (vars.create ? createUser(vars.create) : updateUser(user!.id, vars.patch!)),
    onSuccess: onSaved,
    onError: (e) => setFormError(e instanceof AdminApiError ? e.message : 'Save failed'),
  });

  function toggleReport(id: string) {
    setReports((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function addCustomReport() {
    const id = customReport.trim().toLowerCase();
    if (!id) return;
    if (!REPORT_ID_RE.test(id)) {
      setFormError(`Invalid report id "${id}" — lowercase letters, digits and dashes only.`);
      return;
    }
    setReports((prev) => new Set(prev).add(id));
    setCustomReport('');
    setFormError(null);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * A generated password stays MASKED. The admin's normal path is Generate →
   * Copy → paste into a message, which never needs the characters on screen —
   * and an admin screen is exactly where a password should not sit in plain
   * text (shoulders, screen shares, screenshots). The eye reveals it for the
   * cases that do need reading it out.
   *
   * It is never stored in plaintext anywhere — the backend hashes it (bcrypt) —
   * so this modal is the only place the value exists, hence the note below.
   */
  function onGenerate() {
    setPassword(generatePassword());
    setRevealPassword(false);
    setGenerated(true);
    setCopied(null);
    setFormError(null);
  }

  async function onCopy() {
    try {
      // Only available over HTTPS / localhost; the fallback is "select it
      // yourself", which is why the field is revealed rather than hidden.
      await navigator.clipboard.writeText(password);
      setCopied('ok');
    } catch {
      setCopied('fail');
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);

    if (!EMAIL_RE.test(email.trim())) return setFormError('Enter a valid email address.');
    if (clientChoice === '') return setFormError('Select a client id.');
    if (!CLIENT_ID_RE.test(clientId.trim())) return setFormError('Client id must be lowercase letters, digits, _ or - (starting with a letter).');
    if (mode === 'create' && password.length < 8) return setFormError('Password is required, minimum 8 characters.');
    if (mode === 'edit' && password.length > 0 && password.length < 8) return setFormError('New password must be at least 8 characters.');

    const grants = role === 'admin' ? [] : [...reports];

    if (mode === 'create') {
      mutation.mutate({
        create: { email: email.trim().toLowerCase(), client_id: clientId.trim(), password, role, allowed_reports: grants },
      });
      return;
    }

    const patch: UpdateUserInput = {
      email: email.trim().toLowerCase(),
      client_id: clientId.trim(),
      allowed_reports: grants,
    };
    if (!isSelf) {
      patch.role = role;
      patch.active = active;
    }
    if (password.length >= 8) patch.password = password;
    mutation.mutate({ patch });
  }

  return (
    <Modal
      title={mode === 'create' ? 'New user' : `Edit ${user?.email}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            loadingText="Saving…"
            onClick={(e) => onSubmit(e as unknown as FormEvent<HTMLFormElement>)}
          >
            {mode === 'create' ? 'Create user' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={onSubmit}>
        <div className={styles.modalFields}>
          <TextInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" required />

          <Select
            label="Client id"
            hint="— which client's users this account belongs to"
            value={clientChoice}
            onChange={(e) => setClientChoice(e.target.value)}
            required
          >
            <option value="" disabled>
              Select a client…
            </option>
            {KNOWN_CLIENT_IDS.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
            <option value={OTHER_CLIENT}>Other…</option>
          </Select>

          {clientChoice === OTHER_CLIENT && (
            <TextInput
              label="Client id (other)"
              hint="— lowercase letters, digits, _ or -"
              value={customClientId}
              onChange={(e) => setCustomClientId(e.target.value)}
              placeholder="carrefour-es"
              autoComplete="off"
              required
            />
          )}

          <div className={styles.passwordBlock}>
            <div className={styles.passwordRow}>
              <TextInput
                className={styles.passwordField}
                label="Password"
                hint={mode === 'edit' ? '— leave blank to keep current' : undefined}
                type={revealPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setGenerated(false);
                  setCopied(null);
                }}
                autoComplete="new-password"
                placeholder={mode === 'edit' ? '••••••••' : `min 8 characters`}
              />
              <div className={styles.passwordActions}>
                <Button
                  variant="ghost"
                  onClick={() => setRevealPassword((v) => !v)}
                  disabled={password.length === 0}
                  title={revealPassword ? 'Hide password' : 'Show password'}
                  aria-label={revealPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={revealPassword}
                >
                  <EyeIcon off={revealPassword} />
                </Button>
                <Button variant="ghost" onClick={onCopy} disabled={password.length === 0} title="Copy password" aria-label="Copy password">
                  {copied === 'ok' ? 'Copied' : 'Copy'}
                </Button>
                <Button variant="ghost" onClick={onGenerate} title={`Generate a random ${DEFAULT_PASSWORD_LENGTH}-character password`}>
                  Generate
                </Button>
              </div>
            </div>
            {generated && <div className={styles.passwordNote}>Copy it now and send it to the user — it is hashed on save and cannot be shown again.</div>}
            {copied === 'fail' && <div className={styles.passwordNote}>Could not reach the clipboard — select the password and copy it manually.</div>}
          </div>

          <Select
            label="Role"
            hint={isSelf ? "— you can't change your own" : undefined}
            value={role}
            onChange={(e) => setRole(e.target.value as 'client' | 'admin')}
            disabled={isSelf}
          >
            <option value="client">client — sees only granted reports</option>
            <option value="admin">admin — IceMobile staff, full access</option>
          </Select>

          {mode === 'edit' && (
            <label className={styles.checkRow}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={isSelf} />
              <span>Active {isSelf && <span className={styles.hint}>(you can&apos;t deactivate yourself)</span>}</span>
            </label>
          )}

          {role === 'admin' ? (
            <div className={styles.note}>Admins can access every report — no individual grants needed.</div>
          ) : (
            <div className={styles.reportsSection}>
              <span className={styles.reportsLabel}>Report access</span>
              <div className={styles.reports}>
                {KNOWN_REPORTS.map((r) => (
                  <label key={r.id} className={styles.reportRow}>
                    <input type="checkbox" checked={reports.has(r.id)} onChange={() => toggleReport(r.id)} />
                    <span>{r.label}</span> <code>{r.id}</code>
                  </label>
                ))}
                {customGranted.map((id) => (
                  <label key={id} className={styles.reportRow}>
                    <input type="checkbox" checked onChange={() => toggleReport(id)} />
                    <span>Other</span> <code>{id}</code>
                  </label>
                ))}
                <div className={styles.customAdd}>
                  <input
                    className={styles.customInput}
                    value={customReport}
                    onChange={(e) => setCustomReport(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomReport();
                      }
                    }}
                    placeholder="add report id served elsewhere…"
                  />
                  <Button variant="ghost" onClick={addCustomReport}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          )}

          {formError && <Alert variant="error">{formError}</Alert>}
        </div>
      </form>
    </Modal>
  );
}

/*
 * Eye / eye-off. Inline SVG rather than an icon dependency — it is the only
 * icon in the admin area, and `currentColor` keeps it in step with the button.
 */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M1.8 12S5.4 5.5 12 5.5 22.2 12 22.2 12 18.6 18.5 12 18.5 1.8 12 1.8 12Z" />
      <circle cx="12" cy="12" r="3.2" />
      {off && <path d="M3.5 3.5l17 17" />}
    </svg>
  );
}
